import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import useCollection from '@/hooks/useCollection';
import usePageMeta from '@/hooks/usePageMeta';
import { getMediaById, getNewsByMediaId, getRelatedMedia } from '@/utils/data';

export default function DetailPage() {
  const { id } = useParams();
  const item = getMediaById(id);
  const { isCollected, toggle } = useCollection();
  const [tab, setTab] = useState<'intro' | 'news'>('intro');
  const [toast, setToast] = useState('');

  const relatedNews = useMemo(() => (item ? getNewsByMediaId(item.id) : []), [item]);
  const relatedMedia = useMemo(() => (item ? getRelatedMedia(item, 6) : []), [item]);

  usePageMeta({
    title: item ? `${item.title} - 影视详情 - 御宅屋` : '影视详情 - 御宅屋',
    description: item
      ? `${item.title}（${item.enTitle}）${item.year}年${item.region}${item.type === 'movie' ? '电影' : '电视剧'}，${item.director}执导，${item.cast.join('、')}主演。${item.summary.slice(0, 70)}`
      : '御宅屋为您提供影视作品详情、剧情简介与相关资讯推荐。',
    keywords: item
      ? `${item.title},${item.title}详情,${item.genres.join(',')},御宅屋影视`
      : '影视详情,御宅屋',
  });

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 1800);
  };

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-950">未找到该影视作品</h1>
        <p className="mt-2 text-sm text-foreground-500">作品可能已下架或链接有误</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-home-4-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const collected = isCollected(item.id);
  const typeLabel = item.type === 'movie' ? '电影' : '电视剧';

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制，快去分享给朋友吧');
    } catch {
      showToast('复制失败，请手动复制浏览器地址');
    }
  };

  return (
    <div className="w-full">
      <div className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
        <img
          src={item.banner}
          alt={`${item.title} 主视觉`}
          title={`${item.title} ${item.genres.join(' ')}`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/30"></div>
      </div>

      <div className="w-full px-4 md:px-8 -mt-28 md:-mt-36 relative z-10">
        <Breadcrumb
          items={[
            { label: typeLabel, to: `/category/${item.type}` },
            { label: item.title },
          ]}
        />

        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <div className="w-40 sm:w-48 md:w-56 shrink-0 mx-auto md:mx-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-300/60 bg-background-200">
              <img
                src={item.cover}
                alt={`${item.title} 海报`}
                title={`${item.title} 影视详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-semibold">
                {typeLabel}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-secondary-500/15 text-secondary-300 border border-secondary-500/30 text-xs">
                {item.year}
              </span>
              <span className="px-2.5 py-1 rounded-md bg-background-200/70 text-foreground-700 text-xs">
                {item.region}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
              {item.title}
            </h1>
            <p className="mt-1.5 text-sm text-foreground-500 tracking-wide">{item.enTitle}</p>

            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-3xl font-bold text-accent-400">
                  {item.rating.toFixed(1)}
                </span>
                <span className="text-xs text-foreground-500">/ 10</span>
              </div>
              <div className="h-8 w-px bg-background-300/50"></div>
              <div>
                <p className="text-xs text-foreground-500">热度</p>
                <p className="text-sm text-foreground-900 font-medium">
                  {item.hot.toLocaleString()}
                </p>
              </div>
              <div className="h-8 w-px bg-background-300/50"></div>
              <div>
                <p className="text-xs text-foreground-500">{item.type === 'movie' ? '片长' : '集数'}</p>
                <p className="text-sm text-foreground-900 font-medium">
                  {item.type === 'movie' ? item.duration : `${item.episodes} 集`}
                </p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <p className="text-foreground-600">
                <span className="text-foreground-500">导演：</span>
                <span className="text-foreground-900">{item.director}</span>
              </p>
              <p className="text-foreground-600">
                <span className="text-foreground-500">主演：</span>
                <span className="text-foreground-900">{item.cast.join('、')}</span>
              </p>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {item.genres.map((g) => (
                <Link
                  key={g}
                  to={`/search?q=${encodeURIComponent(g)}`}
                  className="px-2.5 py-1 rounded-full bg-primary-500/25 text-primary-200 border border-primary-400/40 text-xs hover:bg-primary-500/40 transition-colors"
                >
                  {g}
                </Link>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  const now = toggle(item.id);
                  showToast(now ? '已加入我的收藏' : '已取消收藏');
                }}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  collected
                    ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                    : 'border border-background-300/70 text-foreground-800 hover:border-accent-500/60 hover:text-accent-400'
                }`}
              >
                <i className={collected ? 'ri-bookmark-fill' : 'ri-bookmark-line'}></i>
                {collected ? '已收藏' : '收藏'}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300/70 text-foreground-800 text-sm hover:border-accent-500/60 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
              <Link
                to={`/category/${item.type}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300/70 text-foreground-800 text-sm hover:border-accent-500/60 hover:text-accent-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-apps-2-line"></i>
                更多{typeLabel}
              </Link>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mt-10 md:mt-12">
          <div className="lg:col-span-2 min-w-0">
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70 w-fit mb-5">
              {[
                { key: 'intro', label: '剧情简介' },
                { key: 'news', label: `相关资讯 (${relatedNews.length})` },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key as 'intro' | 'news')}
                  className={`px-5 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    tab === t.key
                      ? 'bg-accent-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'intro' ? (
              <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-7">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">
                  {item.title} 剧情简介
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-[1.9]">{item.summary}</p>
                <p className="mt-4 text-sm text-foreground-600 leading-[1.9]">
                  《{item.title}》（{item.enTitle}）是一部{item.year}年上映的{item.region}
                  {typeLabel}，由{item.director}执导，{item.cast.join('、')}主演，类型涵盖
                  {item.genres.join('、')}。本页面仅提供影视资料与资讯介绍，不提供在线播放与下载服务。
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-md bg-secondary-500/15 text-secondary-300 border border-secondary-500/30 text-xs"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {relatedNews.length > 0 ? (
                  relatedNews.map((n) => (
                    <Link
                      key={n.id}
                      to={`/news/${n.id}`}
                      className="group flex items-center gap-4 p-4 rounded-lg bg-background-100 border border-background-200/70 hover:border-accent-500/50 transition-colors"
                    >
                      <div className="w-28 h-[72px] shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={n.cover}
                          alt={n.title}
                          title={`${n.title} 影视资讯`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-accent-400 transition-colors">
                          {n.title}
                        </h3>
                        <p className="mt-1.5 text-xs text-foreground-500 line-clamp-1">{n.summary}</p>
                        <p className="mt-1 text-xs text-foreground-500">
                          <i className="ri-time-line mr-1"></i>
                          {n.publishedAt}
                        </p>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="rounded-lg bg-background-100 border border-background-200/70 py-16 text-center">
                    <p className="text-sm text-foreground-500">暂无相关资讯</p>
                    <Link
                      to="/news"
                      className="mt-4 inline-flex items-center gap-1 text-sm text-accent-400 hover:underline"
                    >
                      去看看全部资讯
                      <i className="ri-arrow-right-s-line"></i>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          <aside className="min-w-0">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 sticky top-24">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-accent-500"></span>
                相关影视推荐
              </h2>
              <div className="flex flex-col gap-3">
                {relatedMedia.slice(0, 4).map((m) => (
                  <Link
                    key={m.id}
                    to={`/detail/${m.id}`}
                    className="group flex gap-3"
                  >
                    <div className="w-14 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={m.cover}
                        alt={m.title}
                        title={`${m.title} 影视推荐`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="min-w-0 flex flex-col justify-center">
                      <h3 className="text-sm text-foreground-900 line-clamp-1 group-hover:text-accent-400 transition-colors">
                        {m.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">
                        {m.year} · {m.genres[0]}
                      </p>
                      <p className="mt-1 text-xs text-accent-400">
                        <i className="ri-star-fill mr-1"></i>
                        {m.rating.toFixed(1)}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1 h-6 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
              猜你喜欢
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
            {relatedMedia.map((m) => (
              <MovieCard key={m.id} item={m} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-background-100 border border-accent-500/50 text-sm text-foreground-900 shadow-lg animate-fade-up">
          <i className="ri-checkbox-circle-line text-accent-400 mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}